import { apiClient } from '@services/api';

export type WcagVersion = '2.0' | '2.1' | '2.2';
export type WcagLevel = 'A' | 'AA' | 'AAA';

export interface TestConfig {
  runtime?: {
    viewport?: { width: number; height: number } | null;
    timeout?: number | null;
    captureScreenshot?: boolean;
    userAgent?: string | null;
    timezone?: string | null;
    emulateDevice?: string | null;
    authentication?: { username: string; password: string } | null;
    geolocation?: { latitude: number; longitude: number; accuracy?: number } | null;
    headers?: Record<string, string>;
    cookies?: unknown[];
    contextSelector?: string | null;
    callbackUrls?: string[];
  };
  wcag?: {
    version?: WcagVersion;
    level?: WcagLevel;
    excludedCriteria?: string[];
  };
  rules?: {
    disabled?: string[];
  };
}

interface ConfigResponse {
  success: boolean;
  data: { config: TestConfig; defaults?: TestConfig };
}

const orgBase = (orgId: string) => `/v1/organizations/${orgId}/test-config`;
const ctxBase = (orgId: string, contextId: string) =>
  `/v1/organizations/${orgId}/contexts/${contextId}/test-config`;

/**
 * REV-460/461/462: read/write the hierarchical test configuration at the org
 * and context levels, and read the resolved effective config for a context.
 */
/** One WCAG success criterion, for the excluded-criteria type-ahead (REV-486). */
export interface WcagCriterion {
  sc: string;
  level: string;
  title: string;
}

export const testConfigService = {
  /**
   * REV-486: the WCAG catalog for a version, so Excluded success criteria can
   * be a type-ahead rather than a comma-separated box a user fills from memory.
   * Served by the API rather than duplicated here — the server copy is the one
   * that decides what actually gets tested.
   */
  async getWcagCriteria(version?: string): Promise<WcagCriterion[]> {
    const res = await apiClient.get<{ success: boolean; data: WcagCriterion[] }>(
      '/v1/wcag/criteria',
      version ? { version } : {}
    );
    return res.data;
  },

  async getOrgConfig(orgId: string): Promise<{ config: TestConfig; defaults?: TestConfig }> {
    const res = await apiClient.get<ConfigResponse>(orgBase(orgId));
    return res.data;
  },

  async putOrgConfig(orgId: string, config: TestConfig): Promise<TestConfig> {
    const res = await apiClient.put<ConfigResponse>(orgBase(orgId), { config });
    return res.data.config;
  },

  async getContextConfig(
    orgId: string,
    contextId: string
  ): Promise<{ config: TestConfig; defaults?: TestConfig }> {
    const res = await apiClient.get<ConfigResponse>(ctxBase(orgId, contextId));
    return res.data;
  },

  async putContextConfig(
    orgId: string,
    contextId: string,
    config: TestConfig
  ): Promise<TestConfig> {
    const res = await apiClient.put<ConfigResponse>(ctxBase(orgId, contextId), { config });
    return res.data.config;
  },

  /** Resolved org -> context config (no run overrides) — for the run-a-test preview. */
  async getEffectiveConfig(orgId: string, contextId: string): Promise<TestConfig> {
    const res = await apiClient.get<ConfigResponse>(`${ctxBase(orgId, contextId)}/effective`);
    return res.data.config;
  },
};
